import { byId, isAr, message, copyText, trackEvent } from './extra-common.js';

let hasStarted = false;
let isUpdating = false;

// 1. Color State
const state = {
  r: 50,
  g: 109,
  b: 255,
  a: 1.0 // 0 to 1
};

// 2. Named Colors Dictionary (for intelligent naming)
const COLOR_NAMES = [
  { nameAr: 'أسود', nameEn: 'Black', r: 0, g: 0, b: 0 },
  { nameAr: 'أبيض', nameEn: 'White', r: 255, g: 255, b: 255 },
  { nameAr: 'رمادي داكن', nameEn: 'Charcoal', r: 54, g: 69, b: 79 },
  { nameAr: 'رمادي صخري', nameEn: 'Slate Gray', r: 112, g: 128, b: 144 },
  { nameAr: 'رمادي', nameEn: 'Gray', r: 128, g: 128, b: 128 },
  { nameAr: 'فضي', nameEn: 'Silver', r: 192, g: 192, b: 192 },
  { nameAr: 'أحمر صريح', nameEn: 'Pure Red', r: 255, g: 0, b: 0 },
  { nameAr: 'قرمزي', nameEn: 'Crimson', r: 220, g: 20, b: 60 },
  { nameAr: 'عنابي', nameEn: 'Burgundy', r: 128, g: 0, b: 32 },
  { nameAr: 'طوبي', nameEn: 'Brick Red', r: 178, g: 34, b: 34 },
  { nameAr: 'مرجاني', nameEn: 'Coral', r: 255, g: 127, b: 80 },
  { nameAr: 'برتقالي صريح', nameEn: 'Orange', r: 255, g: 165, b: 0 },
  { nameAr: 'كهرماني', nameEn: 'Amber', r: 255, g: 191, b: 0 },
  { nameAr: 'ذهبي', nameEn: 'Gold', r: 255, g: 215, b: 0 },
  { nameAr: 'أصفر صريح', nameEn: 'Yellow', r: 255, g: 255, b: 0 },
  { nameAr: 'ليموني', nameEn: 'Lime', r: 50, g: 205, b: 50 },
  { nameAr: 'أخضر عشبي', nameEn: 'Grass Green', r: 34, g: 139, b: 34 },
  { nameAr: 'أخضر صريح', nameEn: 'Pure Green', r: 0, g: 128, b: 0 },
  { nameAr: 'زمردي', nameEn: 'Emerald', r: 80, g: 200, b: 120 },
  { nameAr: 'نعناعي', nameEn: 'Mint', r: 62, g: 180, b: 137 },
  { nameAr: 'أخضر بترولي', nameEn: 'Teal', r: 0, g: 128, b: 128 },
  { nameAr: 'سماوي فاقع', nameEn: 'Cyan', r: 0, g: 255, b: 255 },
  { nameAr: 'فيروزي', nameEn: 'Turquoise', r: 64, g: 224, b: 208 },
  { nameAr: 'أزرق سماوي', nameEn: 'Sky Blue', r: 135, g: 206, b: 235 },
  { nameAr: 'أزرق مائي', nameEn: 'Dodger Blue', r: 30, g: 144, b: 255 },
  { nameAr: 'أزرق ملكي', nameEn: 'Royal Blue', r: 50, g: 109, b: 255 },
  { nameAr: 'أزرق صريح', nameEn: 'Pure Blue', r: 0, g: 0, b: 255 },
  { nameAr: 'أزرق داكن', nameEn: 'Navy Blue', r: 0, g: 0, b: 128 },
  { nameAr: 'نيلي', nameEn: 'Indigo', r: 75, g: 0, b: 130 },
  { nameAr: 'بنفسجي', nameEn: 'Violet', r: 138, g: 43, b: 226 },
  { nameAr: 'أرجواني صريح', nameEn: 'Purple', r: 128, g: 0, b: 128 },
  { nameAr: 'فوشيا / ماجنتا', nameEn: 'Magenta', r: 255, g: 0, b: 255 },
  { nameAr: 'وردي عميق', nameEn: 'Deep Pink', r: 255, g: 20, b: 147 },
  { nameAr: 'وردي باستيل', nameEn: 'Pastel Pink', r: 255, g: 182, b: 193 },
  { nameAr: 'خوخي', nameEn: 'Peach', r: 255, g: 218, b: 185 },
  { nameAr: 'بني شوكولاتة', nameEn: 'Chocolate Brown', r: 123, g: 63, b: 0 },
  { nameAr: 'بني خردلي', nameEn: 'Ochre', r: 204, g: 119, b: 34 }
];

function getNearestColorName(r, g, b) {
  let minDistance = Infinity;
  let matched = COLOR_NAMES[0];
  for (const item of COLOR_NAMES) {
    const dr = r - item.r;
    const dg = g - item.g;
    const db = b - item.b;
    // Euclidean distance in RGB space
    const dist = dr * dr + dg * dg + db * db;
    if (dist < minDistance) {
      minDistance = dist;
      matched = item;
    }
  }
  return isAr ? `${matched.nameAr} (${matched.nameEn})` : `${matched.nameEn}`;
}

// 3. Conversion Algorithms
function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

function parseHex(input) {
  let hex = input.trim().replace(/^#/, '');
  if (!/^[\da-f]{3,8}$/i.test(hex)) {
    throw new Error('Invalid HEX pattern');
  }
  let r, g, b, a = 1.0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 4) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
    a = parseInt(hex[3] + hex[3], 16) / 255;
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (hex.length === 8) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
    a = parseInt(hex.slice(6, 8), 16) / 255;
  } else {
    throw new Error('Invalid HEX length');
  }
  return { r, g, b, a: Number(a.toFixed(2)) };
}

function rgbToHex(r, g, b, a = 1.0) {
  const toHex = (n) => n.toString(16).padStart(2, '0').toLowerCase();
  let base = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  if (a < 1.0) {
    const alphaHex = Math.round(a * 255).toString(16).padStart(2, '0').toLowerCase();
    base += alphaHex;
  }
  return base;
}

function rgbToHsl(r, g, b) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === rNorm) {
      h = ((gNorm - bNorm) / delta) % 6;
    } else if (max === gNorm) {
      h = (bNorm - rNorm) / delta + 2;
    } else {
      h = (rNorm - gNorm) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  return {
    h,
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

function hslToRgb(h, s, l) {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;
  let rP = 0, gP = 0, bP = 0;

  if (h >= 0 && h < 60) {
    rP = c; gP = x; bP = 0;
  } else if (h >= 60 && h < 120) {
    rP = x; gP = c; bP = 0;
  } else if (h >= 120 && h < 180) {
    rP = 0; gP = c; bP = x;
  } else if (h >= 180 && h < 240) {
    rP = 0; gP = x; bP = c;
  } else if (h >= 240 && h < 300) {
    rP = x; gP = 0; bP = c;
  } else if (h >= 300 && h < 360) {
    rP = c; gP = 0; bP = x;
  }

  return {
    r: Math.round((rP + m) * 255),
    g: Math.round((gP + m) * 255),
    b: Math.round((bP + m) * 255)
  };
}

function rgbToHsv(r, g, b) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;
  let h = 0;
  const s = max === 0 ? 0 : delta / max;
  const v = max;

  if (delta !== 0) {
    if (max === rNorm) {
      h = ((gNorm - bNorm) / delta) % 6;
    } else if (max === gNorm) {
      h = (bNorm - rNorm) / delta + 2;
    } else {
      h = (rNorm - gNorm) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  return {
    h,
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
}

function rgbToCmyk(r, g, b) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const k = 1 - Math.max(rNorm, gNorm, bNorm);
  if (k >= 1) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }
  const c = (1 - rNorm - k) / (1 - k);
  const m = (1 - gNorm - k) / (1 - k);
  const y = (1 - bNorm - k) / (1 - k);
  return {
    c: Math.round(c * 100),
    m: Math.round(m * 100),
    y: Math.round(y * 100),
    k: Math.round(k * 100)
  };
}

// 3.5 OKLCH and Display P3 Modern Color Spaces
function sRgbToLinear(c) {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearRgbToOklab(r, g, b) {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bVal = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  return { L, a, b: bVal };
}

function rgbToOklch(r, g, b, a = 1.0) {
  const lr = sRgbToLinear(r);
  const lg = sRgbToLinear(g);
  const lb = sRgbToLinear(b);
  const { L, a: oklabA, b: oklabB } = linearRgbToOklab(lr, lg, lb);
  const C = Math.hypot(oklabA, oklabB);
  let h = (Math.atan2(oklabB, oklabA) * 180) / Math.PI;
  if (h < 0) h += 360;
  const lPercent = (L * 100).toFixed(1);
  const cVal = C.toFixed(3);
  const hVal = h.toFixed(1);
  return a < 1.0
    ? `oklch(${lPercent}% ${cVal} ${hVal} / ${a})`
    : `oklch(${lPercent}% ${cVal} ${hVal})`;
}

function linearRgbToXyz(r, g, b) {
  const X = 0.4124564 * r + 0.3575761 * g + 0.1804375 * b;
  const Y = 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;
  const Z = 0.0193339 * r + 0.1191920 * g + 0.9503041 * b;
  return { X, Y, Z };
}

function xyzToLinearP3(X, Y, Z) {
  const r = 2.4934969119 * X - 0.9313836179 * Y - 0.4027107845 * Z;
  const g = -0.8294889696 * X + 1.7626640603 * Y + 0.0236246858 * Z;
  const b = 0.0358458302 * X - 0.0761723893 * Y + 0.9568845240 * Z;
  return { r, g, b };
}

function linearToP3Gamma(c) {
  if (c <= 0.0031308) {
    return 12.92 * c;
  }
  return 1.055 * Math.pow(Math.max(0, c), 1 / 2.4) - 0.055;
}

function rgbToDisplayP3(r, g, b, a = 1.0) {
  const lr = sRgbToLinear(r);
  const lg = sRgbToLinear(g);
  const lb = sRgbToLinear(b);
  const { X, Y, Z } = linearRgbToXyz(lr, lg, lb);
  const p3Lin = xyzToLinearP3(X, Y, Z);
  const p3R = clamp(linearToP3Gamma(p3Lin.r), 0, 1).toFixed(3);
  const p3G = clamp(linearToP3Gamma(p3Lin.g), 0, 1).toFixed(3);
  const p3B = clamp(linearToP3Gamma(p3Lin.b), 0, 1).toFixed(3);
  return a < 1.0
    ? `color(display-p3 ${p3R} ${p3G} ${p3B} / ${a})`
    : `color(display-p3 ${p3R} ${p3G} ${p3B})`;
}

// 4. WCAG 2.1 Contrast Ratio
function relativeLuminance(r, g, b) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

function getContrastRatio(l1, l2) {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// 5. Palettes / Harmonies & Tints/Shades
function mixColors(c1, c2, weight) {
  const p = weight / 100;
  return {
    r: Math.round(c1.r * (1 - p) + c2.r * p),
    g: Math.round(c1.g * (1 - p) + c2.g * p),
    b: Math.round(c1.b * (1 - p) + c2.b * p)
  };
}

function calculateHarmonies(h, s, l) {
  return [
    {
      titleAr: 'تكميلي (Complementary)',
      titleEn: 'Complementary',
      colors: [(h + 180) % 360]
    },
    {
      titleAr: 'متماثل (Analogous)',
      titleEn: 'Analogous',
      colors: [(h - 30 + 360) % 360, (h + 30) % 360]
    },
    {
      titleAr: 'ثلاثي (Triadic)',
      titleEn: 'Triadic',
      colors: [(h + 120) % 360, (h + 240) % 360]
    },
    {
      titleAr: 'رباعي (Tetradic)',
      titleEn: 'Tetradic',
      colors: [(h + 90) % 360, (h + 180) % 360, (h + 270) % 360]
    },
    {
      titleAr: 'تكميلي منقسم (Split-Comp)',
      titleEn: 'Split Complementary',
      colors: [(h + 150) % 360, (h + 210) % 360]
    }
  ].map((group) => ({
    title: isAr ? group.titleAr : group.titleEn,
    swatches: group.colors.map((hueVal) => {
      const rgb = hslToRgb(hueVal, s, l);
      return {
        hex: rgbToHex(rgb.r, rgb.g, rgb.b),
        rgb,
        h: hueVal,
        s,
        l
      };
    })
  }));
}

// 6. UI Rendering & Synchronization
function renderStudio() {
  if (isUpdating) return;
  isUpdating = true;

  try {
    const { r, g, b, a } = state;
    const hsl = rgbToHsl(r, g, b);
    const hsv = rgbToHsv(r, g, b);
    const cmyk = rgbToCmyk(r, g, b);
    const hex = rgbToHex(r, g, b, a);
    const hexNoAlpha = rgbToHex(r, g, b, 1.0);

    // Update Swatch & Color Name
    const swatchSolid = byId('cs-swatch-solid');
    if (swatchSolid) {
      swatchSolid.style.backgroundColor = a < 1.0 ? `rgba(${r}, ${g}, ${b}, ${a})` : hexNoAlpha;
    }
    const colorNameEl = byId('cs-color-name');
    if (colorNameEl) {
      colorNameEl.textContent = getNearestColorName(r, g, b);
    }
    const hexTag = byId('cs-color-hex-tag');
    if (hexTag) {
      hexTag.textContent = hex.toUpperCase();
    }

    // Update Quick Inputs
    const pickerInput = byId('cc-picker');
    if (pickerInput && pickerInput.value !== hexNoAlpha) {
      pickerInput.value = hexNoAlpha;
    }
    const pickerSwatch = byId('cs-picker-swatch');
    if (pickerSwatch) {
      pickerSwatch.style.backgroundColor = hexNoAlpha;
    }
    const hexInput = byId('cc-hex');
    if (hexInput && document.activeElement !== hexInput) {
      hexInput.value = (a < 1.0 ? hex.slice(1) : hexNoAlpha.slice(1)).toLowerCase();
    }

    // Update RGB Channel Sliders & Numbers
    const setVal = (id, val) => {
      const el = byId(id);
      if (el && document.activeElement !== el) el.value = val;
    };
    setVal('cs-range-r', r);
    setVal('cs-num-r', r);
    setVal('cs-range-g', g);
    setVal('cs-num-g', g);
    setVal('cs-range-b', b);
    setVal('cs-num-b', b);
    setVal('cs-range-a', Math.round(a * 100));
    setVal('cs-num-a', Math.round(a * 100));

    // Dynamic track gradients for RGB & Alpha sliders
    const rangeR = byId('cs-range-r');
    if (rangeR) rangeR.style.setProperty('--track-bg', `linear-gradient(to right, rgb(0, ${g}, ${b}), rgb(255, ${g}, ${b}))`);
    const rangeG = byId('cs-range-g');
    if (rangeG) rangeG.style.setProperty('--track-bg', `linear-gradient(to right, rgb(${r}, 0, ${b}), rgb(${r}, 255, ${b}))`);
    const rangeB = byId('cs-range-b');
    if (rangeB) rangeB.style.setProperty('--track-bg', `linear-gradient(to right, rgb(${r}, ${g}, 0), rgb(${r}, ${g}, 255))`);
    const rangeA = byId('cs-range-a');
    if (rangeA) rangeA.style.setProperty('--track-bg', `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 0), rgba(${r}, ${g}, ${b}, 1))`);

    // Update HSL Channel Sliders & Numbers
    setVal('cs-range-h', hsl.h);
    setVal('cs-num-h', hsl.h);
    setVal('cs-range-s', hsl.s);
    setVal('cs-num-s', hsl.s);
    setVal('cs-range-l', hsl.l);
    setVal('cs-num-l', hsl.l);

    const rangeS = byId('cs-range-s');
    if (rangeS) rangeS.style.setProperty('--track-bg', `linear-gradient(to right, hsl(${hsl.h}, 0%, ${hsl.l}%), hsl(${hsl.h}, 100%, ${hsl.l}%))`);
    const rangeL = byId('cs-range-l');
    if (rangeL) rangeL.style.setProperty('--track-bg', `linear-gradient(to right, hsl(${hsl.h}, ${hsl.s}%, 0%), hsl(${hsl.h}, ${hsl.s}%, 50%), hsl(${hsl.h}, ${hsl.s}%, 100%))`);

    // Update CMYK display
    const setText = (id, txt) => {
      const el = byId(id);
      if (el) el.textContent = txt;
    };
    setText('cs-val-c', `${cmyk.c}%`);
    setText('cs-val-m', `${cmyk.m}%`);
    setText('cs-val-y', `${cmyk.y}%`);
    setText('cs-val-k', `${cmyk.k}%`);

    // Format Codes
    setText('cc-hex-full', hex);
    setText('cc-rgb', a < 1.0 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`);
    setText('cc-hsl', a < 1.0 ? `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${a})` : `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`);
    setText('cc-hsv', `hsv(${hsv.h}, ${hsv.s}%, ${hsv.v}%)`);
    setText('cc-cmyk', `cmyk(${cmyk.c}%, ${cmyk.m}%, ${cmyk.y}%, ${cmyk.k}%)`);
    setText('cc-css-var', `--color-primary: ${hex};`);
    setText('cc-oklch', rgbToOklch(r, g, b, a));
    setText('cc-p3', rgbToDisplayP3(r, g, b, a));

    // Harmonies Grid
    renderHarmonies(hsl.h, hsl.s, hsl.l);

    // Tints & Shades
    renderTintsAndShades(r, g, b);

    // WCAG Accessibility / Contrast Ratios
    renderContrast(r, g, b);
  } finally {
    isUpdating = false;
  }
}

function renderHarmonies(h, s, l) {
  const container = byId('cs-harmonies-grid');
  if (!container) return;
  const harmonies = calculateHarmonies(h, s, l);

  container.innerHTML = harmonies
    .map(
      (grp) => `
    <div class="cs-harmony-card">
      <span class="cs-harmony-title">${grp.title}</span>
      <div class="cs-harmony-palette">
        ${grp.swatches
          .map(
            (sw) => `
          <button type="button" class="cs-harmony-swatch" data-hex="${sw.hex}" style="background-color: ${sw.hex};" title="${sw.hex} (انقر للاستخدام)" aria-label="${sw.hex}">
            <span class="cs-harmony-label">${sw.hex}</span>
          </button>
        `
          )
          .join('')}
      </div>
    </div>
  `
    )
    .join('');
}

function renderTintsAndShades(r, g, b) {
  const tintsRow = byId('cs-tints-row');
  const shadesRow = byId('cs-shades-row');
  if (!tintsRow || !shadesRow) return;

  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  const steps = [10, 20, 30, 40, 50, 60, 70, 80, 90];

  tintsRow.innerHTML = steps
    .map((step) => {
      const mixed = mixColors({ r, g, b }, white, step);
      const hex = rgbToHex(mixed.r, mixed.g, mixed.b);
      return `<button type="button" class="cs-ladder-swatch" data-hex="${hex}" style="background-color: ${hex};" title="${step}% أبيض: ${hex}" aria-label="${hex}"><span class="cs-step-tag">+${step}%</span></button>`;
    })
    .join('');

  shadesRow.innerHTML = steps
    .map((step) => {
      const mixed = mixColors({ r, g, b }, black, step);
      const hex = rgbToHex(mixed.r, mixed.g, mixed.b);
      return `<button type="button" class="cs-ladder-swatch" data-hex="${hex}" style="background-color: ${hex};" title="${step}% أسود: ${hex}" aria-label="${hex}"><span class="cs-step-tag">-${step}%</span></button>`;
    })
    .join('');
}

function renderContrast(r, g, b) {
  const colorLum = relativeLuminance(r, g, b);
  const whiteLum = 1.0; // relativeLuminance(255, 255, 255)
  const darkLum = relativeLuminance(15, 23, 42); // #0f172a

  const ratioWhite = getContrastRatio(whiteLum, colorLum);
  const ratioDark = getContrastRatio(colorLum, darkLum);

  // Update White Contrast Preview
  const prevWhite = byId('cs-prev-white');
  if (prevWhite) prevWhite.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;

  const ratioWhiteEl = byId('cs-ratio-white');
  if (ratioWhiteEl) ratioWhiteEl.textContent = `${ratioWhite.toFixed(2)} : 1`;

  const badgeWhiteAA = byId('cs-badge-white-aa');
  if (badgeWhiteAA) {
    const pass = ratioWhite >= 4.5;
    badgeWhiteAA.className = `cs-badge ${pass ? 'pass' : 'fail'}`;
    badgeWhiteAA.textContent = pass ? (isAr ? 'AA عادي: متاح ✓' : 'AA Normal: PASS ✓') : (isAr ? 'AA عادي: ضعيف ✕' : 'AA Normal: FAIL ✕');
  }

  const badgeWhiteAAA = byId('cs-badge-white-aaa');
  if (badgeWhiteAAA) {
    const pass = ratioWhite >= 7.0;
    badgeWhiteAAA.className = `cs-badge ${pass ? 'pass' : 'fail'}`;
    badgeWhiteAAA.textContent = pass ? (isAr ? 'AAA فائق: متاح ✓' : 'AAA Large: PASS ✓') : (isAr ? 'AAA فائق: ضعيف ✕' : 'AAA Large: FAIL ✕');
  }

  // Update Dark Contrast Preview
  const prevDark = byId('cs-prev-dark');
  if (prevDark) prevDark.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;

  const ratioDarkEl = byId('cs-ratio-dark');
  if (ratioDarkEl) ratioDarkEl.textContent = `${ratioDark.toFixed(2)} : 1`;

  const badgeDarkAA = byId('cs-badge-dark-aa');
  if (badgeDarkAA) {
    const pass = ratioDark >= 4.5;
    badgeDarkAA.className = `cs-badge ${pass ? 'pass' : 'fail'}`;
    badgeDarkAA.textContent = pass ? (isAr ? 'AA عادي: متاح ✓' : 'AA Normal: PASS ✓') : (isAr ? 'AA عادي: ضعيف ✕' : 'AA Normal: FAIL ✕');
  }

  const badgeDarkAAA = byId('cs-badge-dark-aaa');
  if (badgeDarkAAA) {
    const pass = ratioDark >= 7.0;
    badgeDarkAAA.className = `cs-badge ${pass ? 'pass' : 'fail'}`;
    badgeDarkAAA.textContent = pass ? (isAr ? 'AAA فائق: متاح ✓' : 'AAA Large: PASS ✓') : (isAr ? 'AAA فائق: ضعيف ✕' : 'AAA Large: FAIL ✕');
  }
}

// 7. Palette History
const STORAGE_KEY = 'kazakan_color_studio_saved';

function getSavedColors() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveColorToHistory(hex) {
  const current = getSavedColors().filter((c) => c.toLowerCase() !== hex.toLowerCase());
  current.unshift(hex.toLowerCase());
  if (current.length > 28) current.pop();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Ignore storage quota
  }
  renderSavedPalettes();
}

function clearColorHistory() {
  localStorage.removeItem(STORAGE_KEY);
  renderSavedPalettes();
  message('cc-status', isAr ? 'تم مسح سجل الألوان.' : 'Color history cleared.', 'info');
}

function renderSavedPalettes() {
  const container = byId('cs-saved-palettes');
  if (!container) return;
  const list = getSavedColors();

  if (list.length === 0) {
    container.innerHTML = `<div class=\"cs-history-empty\" id=\"cs-history-empty\">${
      isAr ? 'انقر على زر "حفظ" أعلى الشاشة لإضافة ألوان إلى قائمتك هنا.' : 'Click "Save" above to bookmark colors here.'
    }</div>`;
    return;
  }

  container.innerHTML = list
    .map(
      (hex) => `
    <div class="cs-saved-item">
      <button type="button" class="cs-saved-swatch" data-hex="${hex}" style="background-color: ${hex};" title="${hex}" aria-label="${hex}">
        <span class="cs-saved-hex">${hex}</span>
      </button>
      <button type="button" class="cs-saved-del" data-del-hex="${hex}" title="${isAr ? 'حذف' : 'Remove'}" aria-label="${isAr ? 'حذف' : 'Remove'}">&times;</button>
    </div>
  `
    )
    .join('');
}

// 8. Color Setters from Various Inputs
function applyHex(rawHex) {
  try {
    const parsed = parseHex(rawHex);
    state.r = parsed.r;
    state.g = parsed.g;
    state.b = parsed.b;
    state.a = parsed.a;
    renderStudio();
    message('cc-status', '');
  } catch {
    if (document.activeElement === byId('cc-hex')) {
      message('cc-status', isAr ? 'صيغة HEX غير صحيحة (مثال: #326dff أو 326dff)' : 'Invalid HEX format (e.g. #326dff or 326dff)', 'error');
    }
  }
}

function applyRgb(r, g, b, a = state.a) {
  state.r = clamp(parseInt(r, 10) || 0, 0, 255);
  state.g = clamp(parseInt(g, 10) || 0, 0, 255);
  state.b = clamp(parseInt(b, 10) || 0, 0, 255);
  state.a = clamp(parseFloat(a) || 0, 0, 1);
  renderStudio();
}

function applyHsl(h, s, l) {
  const hVal = clamp(parseInt(h, 10) || 0, 0, 360);
  const sVal = clamp(parseInt(s, 10) || 0, 0, 100);
  const lVal = clamp(parseInt(l, 10) || 0, 0, 100);
  const rgb = hslToRgb(hVal, sVal, lVal);
  state.r = rgb.r;
  state.g = rgb.g;
  state.b = rgb.b;
  renderStudio();
}

// 9. Copy Actions with Visual Confirmation
async function handleCopy(text, triggerButton) {
  if (!text) return;
  try {
    await copyText(text);
    triggerButton.classList.add('copied');
    const originalHtml = triggerButton.innerHTML;
    triggerButton.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg><span>${isAr ? 'تم النسخ ✓' : 'Copied ✓'}</span>`;
    message('cc-status', isAr ? `تم نسخ "${text}" بنجاح في الحافظة.` : `Copied "${text}" to clipboard.`, 'success');
    trackEvent('tool_completed', 'color-converter');

    setTimeout(() => {
      triggerButton.classList.remove('copied');
      triggerButton.innerHTML = originalHtml;
    }, 1500);
  } catch {
    message('cc-status', isAr ? 'تعذر الوصول للحافظة.' : 'Could not access clipboard.', 'error');
    trackEvent('tool_failed', 'color-converter');
  }
}

// 10. Initialization and Event Binding
function init() {
  const markStarted = () => {
    if (!hasStarted) {
      hasStarted = true;
      trackEvent('tool_started', 'color-converter');
    }
  };

  // Color picker & HEX input
  const picker = byId('cc-picker');
  if (picker) {
    picker.addEventListener('input', (e) => {
      markStarted();
      applyHex(e.target.value);
    });
  }

  const hexInput = byId('cc-hex');
  if (hexInput) {
    hexInput.addEventListener('input', (e) => {
      markStarted();
      applyHex(e.target.value);
    });
  }

  // RGB Sliders & Numbers
  const bindSync = (rangeId, numId, onSync) => {
    const range = byId(rangeId);
    const num = byId(numId);
    if (range) {
      range.addEventListener('input', (e) => {
        markStarted();
        if (num) num.value = e.target.value;
        onSync(e.target.value);
      });
    }
    if (num) {
      num.addEventListener('input', (e) => {
        markStarted();
        if (range) range.value = e.target.value;
        onSync(e.target.value);
      });
    }
  };

  bindSync('cs-range-r', 'cs-num-r', (val) => applyRgb(val, state.g, state.b));
  bindSync('cs-range-g', 'cs-num-g', (val) => applyRgb(state.r, val, state.b));
  bindSync('cs-range-b', 'cs-num-b', (val) => applyRgb(state.r, state.g, val));
  bindSync('cs-range-a', 'cs-num-a', (val) => applyRgb(state.r, state.g, state.b, val / 100));

  // HSL Sliders & Numbers
  const currentHsl = () => rgbToHsl(state.r, state.g, state.b);
  bindSync('cs-range-h', 'cs-num-h', (val) => applyHsl(val, currentHsl().s, currentHsl().l));
  bindSync('cs-range-s', 'cs-num-s', (val) => applyHsl(currentHsl().h, val, currentHsl().l));
  bindSync('cs-range-l', 'cs-num-l', (val) => applyHsl(currentHsl().h, currentHsl().s, val));

  // Tabs switching
  document.querySelectorAll('.cs-tab-btn').forEach((tabBtn) => {
    tabBtn.addEventListener('click', () => {
      const tab = tabBtn.dataset.tab;
      document.querySelectorAll('.cs-tab-btn').forEach((b) => {
        b.classList.remove('active');
        b.setAttribute('aria-selected', 'false');
      });
      tabBtn.classList.add('active');
      tabBtn.setAttribute('aria-selected', 'true');

      document.querySelectorAll('.cs-tab-pane').forEach((pane) => {
        pane.classList.remove('active');
        pane.hidden = true;
      });
      const targetPane = byId(`cs-pane-${tab}`);
      if (targetPane) {
        targetPane.classList.add('active');
        targetPane.hidden = false;
      }
    });
  });

  // Action Buttons
  const btnEyedropper = byId('cs-btn-eyedropper');
  if (btnEyedropper) {
    if (!window.EyeDropper) {
      btnEyedropper.style.opacity = '0.5';
      btnEyedropper.title = isAr ? 'أداة القطارة غير مدعومة في هذا المتصفح' : 'EyeDropper API is not supported in this browser';
    }
    btnEyedropper.addEventListener('click', async () => {
      markStarted();
      if (!window.EyeDropper) {
        message('cc-status', isAr ? 'أداة القطارة غير مدعومة في متصفحك الحالي، يرجى استخدام متصفح يدعم EyeDropper API مثل Chrome أو Edge.' : 'EyeDropper is not supported in this browser.', 'info');
        return;
      }
      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        if (result?.sRGBHex) {
          applyHex(result.sRGBHex);
        }
      } catch {
        // User cancelled picker
      }
    });
  }

  const btnRandom = byId('cs-btn-random');
  if (btnRandom) {
    btnRandom.addEventListener('click', () => {
      markStarted();
      state.r = Math.floor(Math.random() * 256);
      state.g = Math.floor(Math.random() * 256);
      state.b = Math.floor(Math.random() * 256);
      renderStudio();
      message('cc-status', isAr ? 'تم توليد لون عشوائي جديد.' : 'New random color generated.', 'info');
    });
  }

  const btnInvert = byId('cs-btn-invert');
  if (btnInvert) {
    btnInvert.addEventListener('click', () => {
      markStarted();
      state.r = 255 - state.r;
      state.g = 255 - state.g;
      state.b = 255 - state.b;
      renderStudio();
      message('cc-status', isAr ? 'تم عكس اللون بنجاح.' : 'Color inverted.', 'info');
    });
  }

  const btnSave = byId('cs-btn-save-palette');
  if (btnSave) {
    btnSave.addEventListener('click', () => {
      markStarted();
      const hex = rgbToHex(state.r, state.g, state.b, state.a);
      saveColorToHistory(hex);
      message('cc-status', isAr ? `تم حفظ اللون ${hex} في قائمتك.` : `Saved ${hex} to palette.`, 'success');
    });
  }

  const btnClearHist = byId('cs-btn-clear-history');
  if (btnClearHist) {
    btnClearHist.addEventListener('click', clearColorHistory);
  }

  // Click on swatch chips (Harmonies, Tints & Shades, History)
  const appContainer = byId('color-converter-app');
  if (appContainer) {
    appContainer.addEventListener('click', (e) => {
      // Swatch color click to apply
      const swatchBtn = e.target.closest('[data-hex]');
      if (swatchBtn && !e.target.closest('.cs-saved-del')) {
        const hex = swatchBtn.dataset.hex;
        if (hex) {
          markStarted();
          applyHex(hex);
          return;
        }
      }

      // Delete item from saved history
      const delBtn = e.target.closest('[data-del-hex]');
      if (delBtn) {
        const delHex = delBtn.dataset.delHex;
        const current = getSavedColors().filter((c) => c.toLowerCase() !== delHex.toLowerCase());
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
        renderSavedPalettes();
        return;
      }

      // Copy Target Click (support both data-copy-target and legacy data-copy)
      const copyBtn = e.target.closest('[data-copy-target], [data-copy]');
      if (copyBtn) {
        const targetId = copyBtn.dataset.copyTarget || copyBtn.dataset.copy;
        const targetEl = byId(targetId);
        if (targetEl) {
          handleCopy(targetEl.textContent.trim(), copyBtn);
        }
      }
    });
  }

  // Copy All Button
  const btnCopyAll = byId('cc-copy-all');
  if (btnCopyAll) {
    btnCopyAll.addEventListener('click', () => {
      markStarted();
      const hex = byId('cc-hex-full')?.textContent || rgbToHex(state.r, state.g, state.b, state.a);
      const rgb = byId('cc-rgb')?.textContent || `rgb(${state.r}, ${state.g}, ${state.b})`;
      const hsl = byId('cc-hsl')?.textContent || '';
      const hsv = byId('cc-hsv')?.textContent || '';
      const cmyk = byId('cc-cmyk')?.textContent || '';
      const cssVar = byId('cc-css-var')?.textContent || '';
      const oklch = byId('cc-oklch')?.textContent || '';
      const p3 = byId('cc-p3')?.textContent || '';

      const fullSummary = [
        `HEX: ${hex}`,
        `RGB: ${rgb}`,
        `HSL: ${hsl}`,
        `HSV: ${hsv}`,
        `CMYK: ${cmyk}`,
        `CSS: ${cssVar}`,
        `OKLCH: ${oklch}`,
        `Display P3: ${p3}`
      ].join('\n');

      handleCopy(fullSummary, btnCopyAll);
    });
  }

  // Initial Boot
  renderStudio();
  renderSavedPalettes();
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
